import { Chess } from 'chess.js';

import type {
  IChessState,
  IMove,
  IMoveResult,
  TBoard,
  Color,
  TMoveError,
  Square,
  PieceSymbol,
} from './types';
import {
  boardFromChess,
  castlingFromChess,
  stateToFen,
} from './chess-js-adapter';
import { DEFAULT_TURN_DURATION_MS } from './constants';

export class ChessGame {
  private chess: Chess;
  private status: IChessState['status'] = 'idle';
  private winner: Color | null = null;
  private whiteTimeMs: number;
  private blackTimeMs: number;
  private turnDurationMs: number;
  private lastMove: IMove | null = null;

  constructor(turnDurationMs: number = DEFAULT_TURN_DURATION_MS) {
    this.chess = new Chess();
    this.whiteTimeMs = turnDurationMs;
    this.blackTimeMs = turnDurationMs;
    this.turnDurationMs = turnDurationMs;
  }

  start(): void {
    this.status = 'playing';
  }

  makeMove(from: Square, to: Square, promotion?: PieceSymbol): IMoveResult {
    if (this.status !== 'playing') {
      return this.createResult(false, 'not_playing');
    }

    if (this.chess.turn() !== 'w') {
      return this.createResult(false, 'not_your_turn');
    }

    try {
      this.chess.move({ from, to, promotion });
    } catch {
      return this.createResult(false, 'invalid_move');
    }

    this.lastMove = { from, to, promotion };
    this.checkGameEnd();
    return this.createResult(true, null);
  }

  applyOpponentMove(
    from: Square,
    to: Square,
    promotion?: PieceSymbol,
  ): IMoveResult {
    if (this.status !== 'playing') {
      return this.createResult(false, 'not_playing');
    }

    try {
      this.chess.move({ from, to, promotion });
    } catch {
      return this.createResult(false, 'invalid_move');
    }

    this.lastMove = { from, to, promotion };
    this.checkGameEnd();
    return this.createResult(true, null);
  }

  getLegalMovesFor(from: Square): IMove[] {
    return this.chess.moves({ square: from, verbose: true }).map((m) => ({
      from: m.from as Square,
      to: m.to as Square,
      promotion: m.promotion as PieceSymbol | undefined,
    }));
  }

  getState(): IChessState {
    const board = boardFromChess(this.chess);
    const castlingRights = castlingFromChess(this.chess);
    const history = this.chess.history({ verbose: true });
    const lastHistoryMove =
      history.length > 0 ? history[history.length - 1] : null;
    const ep = this.chess.fen().split(' ')[3];
    const enPassantTarget = ep && ep !== '-' ? (ep as Square) : null;

    return {
      board,
      currentTurn: this.chess.turn(),
      status: this.status,
      winner: this.winner,
      whiteTimeMs: this.whiteTimeMs,
      blackTimeMs: this.blackTimeMs,
      turnDurationMs: this.turnDurationMs,
      lastMove:
        this.lastMove ??
        (lastHistoryMove
          ? {
              from: lastHistoryMove.from as Square,
              to: lastHistoryMove.to as Square,
              promotion: lastHistoryMove.promotion as PieceSymbol | undefined,
            }
          : null),
      moveCount:
        this.chess.moveNumber() * 2 - (this.chess.turn() === 'w' ? 2 : 1),
      castlingRights,
      enPassantTarget,
    };
  }

  getFen(): string {
    return this.chess.fen();
  }

  setTimeLeft(color: Color, timeMs: number): void {
    if (color === 'w') this.whiteTimeMs = timeMs;
    else this.blackTimeMs = timeMs;
  }

  reset(turnDurationMs?: number): void {
    this.chess = new Chess();
    this.status = 'idle';
    this.winner = null;
    this.lastMove = null;
    this.whiteTimeMs = turnDurationMs ?? this.turnDurationMs;
    this.blackTimeMs = turnDurationMs ?? this.turnDurationMs;
    this.turnDurationMs = turnDurationMs ?? this.turnDurationMs;
  }

  loadPosition(
    board: TBoard,
    overrides?: {
      enPassantTarget?: Square | null;
      lastMove?: IMove | null;
      moveCount?: number;
    },
  ): void {
    const fen = stateToFen({
      board,
      currentPlayer: 0,
      castlingRights: {
        whiteKingside: false,
        whiteQueenside: false,
        blackKingside: false,
        blackQueenside: false,
      },
      moveCount: overrides?.moveCount ?? 0,
    });
    const parts = fen.split(' ');
    if (overrides?.enPassantTarget) {
      parts[3] = overrides.enPassantTarget;
    }
    this.chess = new Chess(parts.join(' '));
    this.status = 'playing';
    this.winner = null;
    this.lastMove = overrides?.lastMove ?? null;
  }

  private checkGameEnd(): void {
    if (this.chess.isCheckmate()) {
      this.status = 'checkmate';
      this.winner = this.chess.turn() === 'w' ? 'b' : 'w';
      return;
    }
    if (this.chess.isStalemate()) {
      this.status = 'stalemate';
      return;
    }
    if (this.chess.isDraw()) {
      this.status = 'draw';
      return;
    }
  }

  private createResult(success: boolean, error: TMoveError): IMoveResult {
    const isGameOver = this.status !== 'playing' && this.status !== 'idle';
    const winner = this.status === 'checkmate' ? this.winner : null;
    const isDraw = this.status === 'draw' || this.status === 'stalemate';
    return {
      success,
      error,
      gameState: this.getState(),
      isGameOver,
      winner,
      isDraw,
    };
  }
}
